/**
 * CONFIGURACIÓN DE APPS (URLs y Labels)
 * 
 * Centraliza la configuración de URLs y labels de cada app.
 * Las URLs pueden venir de variables de entorno o valores por defecto.
 * 
 * @package @shared/navigation
 */

/**
 * Modo de acceso de la app
 * - 'strict': Acceso solo si existe registro en user_domain_map (default)
 * - 'membership': Crea registro automáticamente si no existe (primera vez)
 */
export type AccessMode = 'strict' | 'membership';

export interface AppConfig {
  label: string;
  url: string;
  icon?: string;
  accessMode?: AccessMode; // Modo de acceso (default: 'strict')
  welcomePath?: string; // Ruta de bienvenida cuando se crea acceso automático (ej: '/welcome')
}

/**
 * Obtiene la configuración de una app específica
 * 
 * @param appName - Nombre de la app (ej: 'empresa', 'tienda', 'houseme')
 * @returns Configuración de la app o null si no existe
 */
export function getAppConfig(appName: string | null | undefined): AppConfig | null {
  if (!appName || !appName.trim()) {
    return null;
  }
  
  const configs: Record<string, AppConfig> = {
    empresa: {
      label: 'Empresa',
      url:
        import.meta.env.VITE_EMPRESA_URL ||
        (import.meta.env.DEV ? 'http://localhost:5173/empresa' : 'https://www.theplatform360.app'),
      icon: 'ri-building-line',
      accessMode: 'strict',
      welcomePath: '/create-workspace',
    },
    tienda: {
      label: 'Tienda',
      url:
        import.meta.env.VITE_TIENDA_URL ||
        (import.meta.env.DEV ? 'http://localhost:5173/tienda' : 'https://tienda.elfom.com'),
      icon: 'ri-store-line',
      accessMode: 'strict', // Requiere activación manual en página de bienvenida
      welcomePath: '/welcome', // Ruta de bienvenida para activación
    },
    houseme: {
      label: 'Houseme',
      url:
        import.meta.env.VITE_HOUSEME_URL ||
        (import.meta.env.DEV ? 'http://localhost:5173/houseme' : 'https://houseme.app'),
      icon: 'ri-home-line',
      accessMode: 'strict', // Requiere activación manual en página de bienvenida
      welcomePath: '/welcome', // Ruta de bienvenida para activación
    },
    thefomlabs: {
      label: 'TheFomLabs',
      url:
        import.meta.env.VITE_THEFOMLABS_URL ||
        (import.meta.env.DEV ? 'http://localhost:5179' : 'https://thefomlabs.com'),
      icon: 'ri-global-line',
      accessMode: 'strict', // Requiere activación manual en página de bienvenida
      welcomePath: '/welcome', // Ruta de bienvenida para activación
    },
    traela: {
      label: 'Traela',
      url:
        import.meta.env.VITE_TRAELA_URL ||
        (import.meta.env.DEV ? 'http://localhost:5178' : 'https://traela.app'),
      icon: 'ri-truck-line',
      accessMode: 'strict', // Requiere activación manual en página de bienvenida
      welcomePath: '/welcome', // Ruta de bienvenida para activación
    },
    vehicles: {
      label: 'Vehicles',
      url:
        import.meta.env.VITE_VEHICLES_URL ||
        (import.meta.env.DEV ? 'http://localhost:5180' : 'https://vehicles.elfom.com'),
      icon: 'ri-car-line',
      accessMode: 'strict', // Requiere activación manual en página de bienvenida
      welcomePath: '/welcome', // Ruta de bienvenida para activación
    },
    sonmany: {
      label: 'SonMany',
      url:
        import.meta.env.VITE_SONMANY_URL ||
        (import.meta.env.DEV ? 'http://localhost:5181' : 'https://sonmany.com'),
      icon: 'ri-mic-line',
      accessMode: 'membership', // Acceso automático por primera vez
      welcomePath: '/welcome', // Ruta de bienvenida para primer acceso
    },
    gobscore: {
      label: 'GobScore',
      url:
        import.meta.env.VITE_GOBSCORE_URL ||
        (import.meta.env.DEV ? 'http://localhost:5183' : 'https://gobscore.org'),
      icon: 'ri-map-pin-line',
      accessMode: 'membership',
    },
  };

  return configs[appName] || null;
}

/**
 * Obtiene todas las configuraciones de apps disponibles
 */
export function getAllAppConfigs(): Record<string, AppConfig> {
  return {
    empresa: getAppConfig('empresa')!,
    tienda: getAppConfig('tienda')!,
    houseme: getAppConfig('houseme')!,
    thefomlabs: getAppConfig('thefomlabs')!,
  };
}
